import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { Badge, Button, Card, EmptyState, ErrorText, Field, Select, Spinner, StatTile, Table } from '../../components/ui'
import { Icon } from '../../components/ui/icons'
import { FOLLOW_UP_STATUS, formatDate, formatRut } from '../../utils/format'

interface FollowUpRow {
  id: string
  suggestedDate: string
  intervalLabel?: string | null
  reason?: string | null
  status: string
  patient: { id: string; rut: string; firstName: string; lastName: string; phone?: string | null; email?: string | null }
  professional?: { firstName: string; lastName: string } | null
  appointment?: { id: string; startsAt: string; status: string } | null
}

/** Número chileno en formato de enlace de WhatsApp (solo dígitos). */
function whatsappLink(phone: string, text: string): string {
  return `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`
}

/**
 * Lista de gestión de próximos controles (P2). Es la pantalla que recepción
 * trabaja: a quién hay que llamar esta semana porque el médico indicó control
 * y todavía no tiene hora.
 */
export function ControlesPage() {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState('PENDING')
  const [horizon, setHorizon] = useState('30')
  const [error, setError] = useState('')

  const until = new Date(Date.now() + Number(horizon) * 86_400_000).toISOString().slice(0, 10)

  const followUps = useQuery({
    queryKey: ['follow-ups', 'board', status, horizon],
    queryFn: async () =>
      (await api.get<FollowUpRow[]>('/follow-ups', { params: { ...(status ? { status } : {}), until } })).data,
  })

  const update = useMutation({
    mutationFn: async ({ id, status: next }: { id: string; status: string }) =>
      (await api.patch(`/follow-ups/${id}`, { status: next })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['follow-ups'] }),
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const list = followUps.data ?? []
  const today = new Date().toISOString().slice(0, 10)
  const overdue = list.filter((f) => f.suggestedDate.slice(0, 10) < today && f.status === 'PENDING')

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">Próximos controles</h1>
        <p className="text-sm text-steel-600">
          Controles indicados en la ficha que todavía hay que agendar. El paciente también los ve en su portal.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile label="En el período" value={list.length} accent="brand" />
        <StatTile label="Vencidos sin agendar" value={overdue.length} accent={overdue.length > 0 ? 'danger' : 'ink'} />
        <StatTile label="Ya agendados" value={list.filter((f) => f.status === 'SCHEDULED').length} accent="success" />
      </div>

      <Card>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Estado">
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="PENDING">Por agendar</option>
              <option value="SCHEDULED">Agendados</option>
              <option value="">Todos</option>
            </Select>
          </Field>
          <Field label="Horizonte">
            <Select value={horizon} onChange={(e) => setHorizon(e.target.value)}>
              <option value="7">Próximos 7 días</option>
              <option value="30">Próximos 30 días</option>
              <option value="90">Próximos 90 días</option>
              <option value="3650">Todos</option>
            </Select>
          </Field>
        </div>
      </Card>

      <ErrorText>{error}</ErrorText>

      <Card title="Pendientes de gestión">
        {followUps.isLoading ? (
          <Spinner />
        ) : list.length === 0 ? (
          <EmptyState message="No hay controles pendientes en este período." />
        ) : (
          <Table headers={['Fecha sugerida', 'Paciente', 'Motivo', 'Indicó', 'Estado', 'Contacto', '']}>
            {list.map((f) => {
              const late = f.suggestedDate.slice(0, 10) < today && f.status === 'PENDING'
              return (
                <tr key={f.id} className={late ? 'bg-amber-50' : undefined}>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {formatDate(f.suggestedDate)}
                    {late && <span className="ml-1 text-xs font-semibold text-amber-700">vencido</span>}
                  </td>
                  <td className="px-2 py-2">
                    <Link to={`/app/pacientes/${f.patient.id}`} className="font-medium text-brand-700 hover:underline">
                      {f.patient.firstName} {f.patient.lastName}
                    </Link>
                    <span className="block text-xs text-gray-500">{formatRut(f.patient.rut)}</span>
                  </td>
                  <td className="px-2 py-2">
                    {f.reason ?? 'Control'}
                    {f.intervalLabel && <span className="block text-xs text-gray-500">{f.intervalLabel}</span>}
                  </td>
                  <td className="px-2 py-2 text-gray-600">
                    {f.professional ? `${f.professional.firstName} ${f.professional.lastName}` : '—'}
                  </td>
                  <td className="px-2 py-2">
                    <Badge className={FOLLOW_UP_STATUS[f.status]?.className}>{FOLLOW_UP_STATUS[f.status]?.label}</Badge>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      {f.patient.phone && (
                        <>
                          <a href={`tel:${f.patient.phone}`} className="text-brand-700 hover:underline" title="Llamar">
                            <Icon name="phone" className="h-4 w-4" />
                          </a>
                          <a
                            href={whatsappLink(
                              f.patient.phone,
                              `Hola ${f.patient.firstName}, te escribimos de Cima Sport para agendar tu control indicado para el ${formatDate(f.suggestedDate)}.`,
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-700 hover:underline"
                            title="Escribir por WhatsApp"
                          >
                            <Icon name="whatsapp" className="h-4 w-4" />
                          </a>
                        </>
                      )}
                      {!f.patient.phone && <span className="text-xs text-gray-400">Sin teléfono</span>}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex gap-1">
                      <Link
                        to={`/app/agenda?patientId=${f.patient.id}`}
                        className="rounded-lg px-2.5 py-1.5 text-sm font-medium text-brand-700 hover:bg-soft"
                      >
                        Agendar
                      </Link>
                      <Button variant="ghost" onClick={() => update.mutate({ id: f.id, status: 'DONE' })}>
                        Realizado
                      </Button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </Table>
        )}
      </Card>
    </div>
  )
}
